<app-header></app-header>
<div class="faq-wrapper">

  <!-- HERO -->
  <section class="faq-hero" aria-labelledby="faq-hero-title">
    <div class="faq-hero-bg"></div>
    <div class="faq-hero-overlay"></div>

    <div class="faq-hero-inner">
      <div class="faq-pill-eyebrow">
        <span class="faq-pill-dot"></span>
        Frequently Asked Questions
      </div>

      <h1 id="faq-hero-title" class="faq-hero-title">
        Big questions,<br /><em>simple answers.</em>
      </h1>

      <p class="faq-hero-sub">
        A lot of people want to become a coach, but they feel confused.
        This page gives simple answers about the ICF, the programs,
        and how NEXGEN Executive Coaching Academy helps you become a coach and build a business.
      </p>

      <div class="faq-hero-cta">
        <a
          href="https://academy.nexgencoaches.com/live-session"
          target="_blank"
          rel="noopener noreferrer"
          class="btn-gold-solid">
          ATTEND FREE WEBINAR
        </a>

        <a
          href="https://academy.nexgencoaches.com/icf-overview"
          target="_blank"
          rel="noopener noreferrer"
          class="btn-white-outline">
          EXPLORE ICF OVERVIEW →
        </a>
      </div>
    </div>
  </section>

  <!-- AT A GLANCE -->
  <section class="cream-section faq-top-summary">
    <div class="cx">
      <p class="gold-eyebrow">FAQ OVERVIEW</p>
      <h2>What this page <em>covers.</em></h2>
      <p class="section-lead">
        This page gives quick answers about ICF, NEXGEN Executive Coaching programs, the platform,
        pricing, and building a coaching business.
      </p>

      <div class="accordion-rows">
        <div class="acc-row">
          <div class="acc-num">1</div>
          <div class="acc-divider"></div>
          <div class="acc-body">
            <strong>About ICF</strong>
            <p>Learn what ICF means, what ACC, PCC, and MCC are, and why ICF matters.</p>
          </div>
          <div class="acc-result">
            <em>Clear answers for people who want a real coaching path.</em>
          </div>
        </div>

        <div class="acc-row">
          <div class="acc-num">2</div>
          <div class="acc-divider"></div>
          <div class="acc-body">
            <strong>Programs</strong>
            <p>Understand the difference between Silver, Gold, and Platinum and which path fits your goals.</p>
          </div>
          <div class="acc-result">
            <em>Start where you are. Grow when you are ready.</em>
          </div>
        </div>

        <div class="acc-row">
          <div class="acc-num">3</div>
          <div class="acc-divider"></div>
          <div class="acc-body">
            <strong>Platform & Community</strong>
            <p>See how learning, live sessions, and community support work together.</p>
          </div>
          <div class="acc-result">
            <em>Built for real people with real schedules.</em>
          </div>
        </div>

        <div class="acc-row">
          <div class="acc-num">4</div>
          <div class="acc-divider"></div>
          <div class="acc-body">
            <strong>Business & Pricing</strong>
            <p>Get clear answers about building a coaching business, costs, upgrades, and payment questions.</p>
          </div>
          <div class="acc-result">
            <em>Be a coach. Build a business.</em>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- CATEGORY TABS -->
  <div class="faq-tabs-bar" *ngIf="!searchTerm">
    <button
      *ngFor="let cat of categories"
      class="faq-tab"
      [class.active]="activeCategory === cat.id"
      (click)="setCategory(cat.id)">
      <i [class]="cat.icon"></i>
      {{ cat.label }}
      <span class="tab-count">{{ cat.count }}</span>
    </button>
  </div>

  <!-- FAQ GROUPS -->
  <section class="white-section faq-main-section">
    <div class="cx">

      <ng-container *ngFor="let group of visibleGroups()">
        <div
          class="faq-group"
          *ngIf="group.items.length > 0"
          [attr.aria-labelledby]="'faq-group-title-' + group.id">

          <div class="faq-group-intro">
            <p class="gold-eyebrow">{{ group.title }}</p>
            <h2 [id]="'faq-group-title-' + group.id">
              {{ group.title.split(' ')[0] }}
              <em>{{ group.title.substring(group.title.indexOf(' ') + 1) }}</em>
            </h2>
            <p class="section-lead faq-group-lead">{{ group.subtitle }}</p>
          </div>

          <div class="faq-list">
            <div
              *ngFor="let item of group.items; let i = index"
              class="faq-item"
              [class.open]="item.open"
              (click)="toggle(item)">

              <div class="faq-question">
                <div class="faq-question-left">
                  <div class="faq-q-number">{{ i + 1 < 10 ? '0' + (i + 1) : i + 1 }}</div>
                  <span>{{ item.q }}</span>
                </div>

                <div class="faq-chevron">
                  <i class="fa-solid fa-chevron-down"></i>
                </div>
              </div>

              <div class="faq-answer" [style.maxHeight]="item.open ? '700px' : '0px'">
                <div class="faq-answer-inner" [innerHTML]="item.a"></div>
              </div>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- No results -->
      <div class="faq-empty" *ngIf="filteredCount === 0 && searchTerm">
        <div class="empty-icon"><i class="fa-solid fa-magnifying-glass"></i></div>
        <h3>No results for "{{ searchTerm }}"</h3>
        <p>Try a different search term, or browse by category above.</p>
        <button class="btn-clear-search" (click)="clearSearch()">
          <i class="fa-solid fa-rotate-left"></i>
          Browse All Questions
        </button>
      </div>

    </div>
  </section>

  <!-- MOST ASKED -->
  <section class="cream-section faq-most-asked">
    <div class="cx">
      <p class="gold-eyebrow">MOST ASKED</p>
      <h2>Start with these <em>three questions.</em></h2>
      <p class="section-lead">
        These are the questions people usually want answered first.
      </p>

      <div class="feature-table faq-feature-table">
        <div class="ft-row">
          <div class="ft-num-col"><span class="ft-num">01</span></div>
          <div class="ft-divider-col"><div class="ft-line"></div></div>
          <div class="ft-main-col">
            <strong>Which program should I choose: Silver, Gold, or Platinum?</strong>
            <p>Choose Silver if you want to start slowly, Gold if you want the clearest ICF path and business support, and Platinum if you want the more advanced PCC pathway.</p>
          </div>
          <div class="ft-result-col">
            <div class="result-quote"><em>Most people choose the path that fits where they are right now.</em></div>
          </div>
        </div>

        <div class="ft-row">
          <div class="ft-num-col"><span class="ft-num">02</span></div>
          <div class="ft-divider-col"><div class="ft-line"></div></div>
          <div class="ft-main-col">
            <strong>Can I upgrade later?</strong>
            <p>Yes. NEXGEN Executive Coaching Academy is built so you can start where you are and move forward when you are ready.</p>
          </div>
          <div class="ft-result-col">
            <div class="result-quote"><em>You do not lose progress. You keep building forward.</em></div>
          </div>
        </div>

        <div class="ft-row">
          <div class="ft-num-col"><span class="ft-num">03</span></div>
          <div class="ft-divider-col"><div class="ft-line"></div></div>
          <div class="ft-main-col">
            <strong>What is the Barter Coaching Platform?</strong>
            <p>The Barter Coaching Platform helps students coach each other, build confidence, and log coaching hours with real people.</p>
          </div>
          <div class="ft-result-col">
            <div class="result-quote"><em>It helps solve one of the biggest problems new coaches face: getting hours.</em></div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section class="faq-cta">
    <div class="faq-cta-inner">
      <div class="faq-cta-icon"><i class="fa-solid fa-headset"></i></div>

      <div class="faq-cta-text">
        <p class="gold-eyebrow">STILL NEED HELP?</p>
        <h2>Still have <em>questions?</em></h2>
        <p>We are here to help. Reach out to NEXGEN Executive Coaching Academy or join a free webinar and ask us live.</p>
      </div>

      <div class="faq-cta-actions">
        <a
          href="https://nexgencoaches.com/contact-us/"
          target="_blank"
          rel="noopener noreferrer"
          class="btn-contact">
          <i class="fa-solid fa-envelope"></i>
          Contact Us
        </a>

        <a
          href="https://academy.nexgencoaches.com/live-session"
          target="_blank"
          rel="noopener noreferrer"
          class="btn-webinar">
          <i class="fa-solid fa-video"></i>
          Ask at Free Webinar
        </a>
      </div>
    </div>
  </section>

</div>
<app-bottomside></app-bottomside>